import React, { useState, useEffect, useCallback } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Box, Tabs, Tab } from "@mui/material";
import { getCourtStartTime, getCrewSchedule } from "../firebaseService";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];
const CREW_LABELS = ["A", "B", "C", "D"];
const CREW_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2"];

const timeToMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

const CourtCrewTimes = () => {
  const { daysOfWeek } = useTournamentConfig();
  const [loading, setLoading] = useState(true);
  const [courtTimes, setCourtTimes] = useState({});
  const [flexSchedule, setFlexSchedule] = useState(null);
  const [selectedDay, setSelectedDay] = useDefaultTournamentDay(daysOfWeek);
  const endHour = 20;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Try flexible schedule first
      const schedule = await getCrewSchedule(selectedDay);
      if (schedule && schedule.slots && schedule.mode !== "simple") {
        setFlexSchedule(schedule);
        setCourtTimes({});
      } else {
        setFlexSchedule(null);
        // Fall back to simple mode
        const data = {};
        await Promise.all(courts.map(async (court) => {
          const courtData = await getCourtStartTime(selectedDay, court);
          if (courtData.startTime) {
            const [startHour, startMinute] = courtData.startTime.split(":").map(Number);
            const timeslots = [];
            const interval = courtData.isTurnusEnabled ? 45 : 60;
            const end = new Date();
            end.setHours(endHour, 0, 0, 0);
            const current = new Date();
            current.setHours(startHour, startMinute, 0, 0);

            while (current <= end) {
              const hh = String(current.getHours()).padStart(2, "0");
              const mm = String(current.getMinutes()).padStart(2, "0");
              timeslots.push(`${hh}:${mm}`);
              current.setMinutes(current.getMinutes() + interval);
            }

            if (courtData.hasThreeCrews) {
              data[court] = {
                timeslots,
                "Crew A": timeslots.map((_, i) => (i % 3 === 0 ? court : "Pause")),
                "Crew B": timeslots.map((_, i) => (i % 3 === 1 ? court : "Pause")),
                "Crew C": timeslots.map((_, i) => (i % 3 === 2 ? court : "Pause")),
              };
            } else {
              data[court] = {
                timeslots,
                "Crew A": timeslots.map((_, i) => (i % 2 === 0 ? court : "Pause")),
                "Crew B": timeslots.map((_, i) => (i % 2 === 1 ? court : "Pause")),
              };
            }
          }
        }));
        setCourtTimes(data);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    }
    setLoading(false);
  }, [selectedDay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build flexible timeline
  const renderFlexSchedule = () => {
    const { slots, crewCount = 2 } = flexSchedule;
    const allSlots = [];
    Object.entries(slots).forEach(([crew, crewSlots]) => {
      (crewSlots || []).forEach((slot) => {
        allSlots.push({ crew, ...slot });
      });
    });

    if (allSlots.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
          Keine Einsaetze fuer {selectedDay} konfiguriert.
        </Typography>
      );
    }

    // Build 30-min timeline
    const times = new Set();
    allSlots.forEach((s) => { times.add(s.from); times.add(s.to); });
    const sortedTimes = Array.from(times).sort();
    const earliest = sortedTimes[0];
    const latest = sortedTimes[sortedTimes.length - 1];
    const [eh, em] = earliest.split(":").map(Number);
    const [lh, lm] = latest.split(":").map(Number);
    const timeline = [];
    let cur = eh * 60 + em;
    const end = lh * 60 + lm;
    while (cur < end) {
      const hh = String(Math.floor(cur / 60)).padStart(2, "0");
      const mm = String(cur % 60).padStart(2, "0");
      timeline.push(`${hh}:${mm}`);
      cur += 30;
    }

    const getSlotAt = (crew, timeStr) => {
      const t = timeToMinutes(timeStr);
      return allSlots.find((s) => s.crew === crew && timeToMinutes(s.from) <= t && timeToMinutes(s.to) > t);
    };

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ textAlign: "center", mt: 1 }}>
          Crew-Einsatzplan - {selectedDay}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 2 }}>Crew</TableCell>
              {timeline.map((t) => (
                <TableCell key={t} align="center" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>{t}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {CREW_LABELS.slice(0, crewCount).map((crew, crewIdx) => (
              <TableRow key={crew}>
                <TableCell sx={{ fontWeight: 700, color: CREW_COLORS[crewIdx], position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 1 }}>
                  Crew {crew}
                </TableCell>
                {timeline.map((t) => {
                  const slot = getSlotAt(crew, t);
                  return (
                    <TableCell
                      key={t}
                      align="center"
                      sx={{
                        fontSize: "0.7rem",
                        bgcolor: slot ? CREW_COLORS[crewIdx] : "transparent",
                        color: slot ? "white" : "text.disabled",
                        py: 0.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {slot ? slot.court : "Pause"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
        Startzeiten der Crews pro Court - {selectedDay}
      </Typography>

      <Tabs value={selectedDay} onChange={(_, v) => setSelectedDay(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3 }} centered>
        {daysOfWeek.map((day) => (
          <Tab key={day} label={day} value={day} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress style={{ display: "block", margin: "20px auto" }} />
      ) : flexSchedule ? (
        renderFlexSchedule()
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
          {courts.map((court) => (
            courtTimes[court] && (
              <TableContainer key={court} component={Paper} style={{ marginTop: 20, marginBottom: 20, width: "100%" }}>
                <Typography variant="h6" style={{ textAlign: "center", marginTop: "10px" }}>{court}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zeit</TableCell>
                      {courtTimes[court].timeslots.map((time) => (
                        <TableCell key={time}>{time}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(courtTimes[court]).filter(key => key !== "timeslots").map((crew) => (
                      <TableRow key={crew}>
                        <TableCell>{crew}</TableCell>
                        {courtTimes[court][crew].map((slot, index) => (
                          <TableCell key={index}>{slot}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ))}
          {Object.keys(courtTimes).length === 0 && (
            <Typography color="text.secondary" sx={{ mt: 4 }}>
              Keine Startzeiten fuer {selectedDay} konfiguriert.
            </Typography>
          )}
        </div>
      )}
    </Box>
  );
};

export default CourtCrewTimes;
